// Prices are always captured from the sales rep in USD/lb, but each customer is
// configured with its own currency and unit of measure (see `customers.currency`
// and `customers.unit_of_measure`). Whenever either of those differs from USD/lb
// the captured price has to be converted before it is stored in
// `opportunities.fixed_price`.
//
// The rates come from Api::V1::ExchangeRatesController#rate_per_currency_unit:
//   - `measurementRate`: units of the customer unit contained in 1 lb
//     (e.g. lb -> kg => 0.4536), from Weight.conversion_factor.
//   - `customerRate`: customer currency -> USD, i.e. the *inverse* of the
//     exchange rate returned by the endpoint (USD -> customer currency).

export const BASE_CURRENCY = 'USD';
export const BASE_UNIT_OF_MEASURE = 'unit_lb';

export const isBaseCurrency = (currency?: string): boolean => currency === BASE_CURRENCY;

export const isBaseUnitOfMeasure = (unitOfMeasure?: string): boolean =>
  unitOfMeasure === BASE_UNIT_OF_MEASURE;

/**
 * A conversion is needed when the customer differs from USD/lb in *either*
 * dimension. Requiring both to differ (`!sameCurrency && !sameUnitOfMeasure`)
 * silently skipped customers such as USD/kg — see PLA-1005.
 */
export const needsPriceConversion = (
  sameCurrency: boolean,
  sameUnitOfMeasure: boolean
): boolean => !sameCurrency || !sameUnitOfMeasure;

interface ConversionRates {
  measurementRate: number | string;
  customerRate: number | string;
}

/** USD/lb -> customer currency per customer unit (what `fixed_price` stores). */
export const usdLbToCustomerPrice = (
  priceUsdLb: number | string,
  { measurementRate, customerRate }: ConversionRates
): number => {
  const pricePerCustomerUnit = Number(priceUsdLb) / Number(measurementRate);

  return pricePerCustomerUnit / Number(customerRate);
};

/** Inverse of `usdLbToCustomerPrice`, used by local contracts. */
export const customerPriceToUsdLb = (
  customerPrice: number | string,
  { measurementRate, customerRate }: ConversionRates
): number => {
  const priceInUsd = Number(customerPrice) * Number(customerRate);

  return priceInUsd * Number(measurementRate);
};
